/**
 * ConnectMetaButton — Boton que inicia el flujo OAuth Meta.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_API_URL || '/api';
const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 750;
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;

export default function ConnectMetaButton({
  workspaceId,
  onSuccess,
  onError,
  variant = 'default',
  size = 'sm',
  className = '',
  children,
  ...rest
}) {
  const [loading, setLoading] = useState(false);
  const popupRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMessage = useCallback((event) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data;
    if (!data || data.type !== 'OAUTH_RESULT' || data.provider !== 'meta') return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setLoading(false);

    if (data.status === 'success' && data.identity_id) {
      toast.success('Meta conectado correctamente');
      onSuccess?.(data.identity_id);
    } else {
      const reason = data.reason || 'Error desconocido';
      toast.error(`Error conectando Meta: ${reason}`);
      onError?.(reason);
    }
  }, [onSuccess, onError]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [handleMessage]);

  const handleClick = async () => {
    if (!workspaceId) {
      toast.error('Falta workspaceId');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/v2/oauth/meta/start?workspace_id=${encodeURIComponent(workspaceId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      const { auth_url } = await res.json();
      if (!auth_url) throw new Error('Respuesta sin auth_url');

      const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
      const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
      const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,location=yes`;
      popupRef.current = window.open(auth_url, 'meta_oauth_popup', features);

      if (!popupRef.current) {
        setLoading(false);
        toast.error('El popup fue bloqueado. Permite popups e intenta de nuevo.');
        onError?.('popup_blocked');
        return;
      }
      try { popupRef.current.focus(); } catch (e) {}

      timeoutRef.current = setTimeout(() => {
        if (popupRef.current && !popupRef.current.closed) {
          try { popupRef.current.close(); } catch (e) {}
        }
        setLoading(false);
        toast.error('Tiempo agotado conectando Meta');
        onError?.('timeout');
      }, POPUP_TIMEOUT_MS);

      const pollInterval = setInterval(() => {
        if (!popupRef.current || popupRef.current.closed) {
          clearInterval(pollInterval);
          setTimeout(() => {
            setLoading(prev => {
              if (prev && timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              return false;
            });
          }, 1500);
        }
      }, 500);
    } catch (e) {
      setLoading(false);
      toast.error(`Error iniciando OAuth: ${e.message}`);
      onError?.(e.message);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleClick}
      disabled={loading}
      {...rest}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Conectando...
        </>
      ) : (
        children || (
          <>
            <Plus className="h-4 w-4 mr-2" />
            Conectar Meta
          </>
        )
      )}
    </Button>
  );
}
